import { ElMessage } from 'element-plus'
import http from './http'
import { isChina } from './location'
import { tryGetAuthUrl } from './resource-url'
import uploadToAliyunOss, { AliyunUploadModule } from './oss-aliyun'

declare global {
  interface Window {
    /** 调试开关：控制台执行 window.__USE_ALIYUN_OSS__ = true 后，上传强制走阿里云 OSS */
    __USE_ALIYUN_OSS__?: boolean
  }
}

/**
 * 上传业务模块固定类型
 */
export const UploadModule = {
  /** 练习 */
  PRACTICE: 'practice',
  /** 模考 */
  MOCK: 'mock',
  /** 成绩单 */
  REPORT: 'report',
  /** AI反馈 */
  AI_FEEDBACK: 'ai-feedback'
} as const

export type UploadModuleType = (typeof UploadModule)[keyof typeof UploadModule]

interface PostPolicyData {
  cosHost: string
  cosKey: string
  policy: string
  domain: string
  qsignAlgorithm: string
  qsignature: string
  qak: string
  qkeyTime: string
}

/**
 * 判断是否国内网络环境（与 wav-record.ts 中逻辑一致）
 * 通过时区判断，pte-api.ytaxx.com、www-singapore.ytaxx.com 强制视为海外
 */
const isZH = (() => {
  const timeZone = -new Date().getTimezoneOffset() / 60
  if (['pte-api.ytaxx.com', 'www-singapore.ytaxx.com'].includes(window.location.host)) {
    return false
  }
  return timeZone === 8
})()

/**
 * COS 默认上传域名 -> 全球加速域名映射
 * 根据 policy.cosHost 匹配，海外环境（时区/域名判断非国内，或 IP 属地非中国）默认直接使用加速域名，
 * 国内环境下默认域名上传失败时自动切换
 */
const COS_ACCELERATE_HOST_MAP: Record<string, string> = {
  'audio-1313503552.cos.ap-guangzhou.myqcloud.com': 'audio-1313503552.cos.accelerate.myqcloud.com',
  'image-1313503552.cos.ap-guangzhou.myqcloud.com': 'image-1313503552.cos.accelerate.myqcloud.com',
  'doc-1313503552.cos.ap-guangzhou.myqcloud.com': 'doc-1313503552.cos.accelerate.myqcloud.com'
}

/**
 * 判断是否海外网络环境
 * 时区/域名判断为非国内，或 IP 属地（App.vue provide 的 isChina 共享值）非中国，均视为海外
 */
const isOverseas = (): boolean => {
  return !isZH || !isChina.value
}

/** 已切换为全球加速域名的 cosHost，后续上传直接使用加速域名（会话级，刷新页面重置） */
const acceleratedHosts = new Set<string>()

const MIME_TO_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'audio/wav': 'wav',
  'audio/wave': 'wav',
  'audio/webm': 'webm',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/mp4': 'mp4',
  'audio/x-m4a': 'm4a',
  'audio/ogg': 'ogg',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'application/pdf': 'pdf',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx'
}

/**
 * 从 File 对象中提取扩展名
 */
const getFileExt = (file: File): string => {
  const fromName = file.name.split('.').pop()?.trim().toLowerCase()
  if (fromName && /^[a-z0-9]{1,10}$/.test(fromName)) {
    return fromName
  }
  if (file.type) {
    return MIME_TO_EXT[file.type] || file.type.split('/').pop() || ''
  }
  return ''
}

/**
 * 获取腾讯云 COS 直传配置（桶、路径、访问域名等）
 * @param module 业务模块名称，使用 UploadModule 常量
 * @param ext 文件扩展名，例如：mp3、wav、jpg、png
 */
const getPostPolicy = (module: UploadModuleType, ext: string): Promise<PostPolicyData> =>
  http
    .get<PostPolicyData>('/api/tencent/v2/postPolicy', {
      module,
      ext
    })
    .then((res) => res.data)

/**
 * 解析 COS 返回的 XML 错误信息
 */
const parseCosError = (xml: string): string => {
  try {
    const parser = new DOMParser()
    const doc = parser.parseFromString(xml, 'application/xml')
    const message = doc.querySelector('Message')?.textContent
    const code = doc.querySelector('Code')?.textContent
    return message || code || xml
  } catch {
    return xml
  }
}

/** COS 上传超时时间（毫秒），超时后按失败处理，切换下一个域名/兜底 */
const UPLOAD_TIMEOUT = 60_000

/**
 * 通过 POST 表单上传文件到指定 COS 域名（60s 超时）
 * 注意：不能用 AbortSignal.timeout（Safari 16+ 才支持），用 AbortController + setTimeout 兼容旧浏览器
 */
const postToCos = async (host: string, formData: FormData): Promise<void> => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT)
  try {
    const res = await fetch(`https://${host}/`, {
      method: 'POST',
      body: formData,
      signal: controller.signal
    })
    if (!res.ok) {
      const text = await res.text()
      throw new Error(parseCosError(text) || `上传失败 (${res.status})`)
    }
  } finally {
    clearTimeout(timer)
  }
}

/**
 * 阿里云 OSS 兜底目前仅支持音频上传（AliyunUploadModule 只有 voice）
 */
const isAudioFile = (file: File, ext: string): boolean =>
  file.type.startsWith('audio/') || ['wav', 'webm', 'mp3', 'm4a', 'ogg'].includes(ext)

/**
 * 上传文件到腾讯云 COS（失败时音频文件自动兜底到阿里云 OSS）
 * 海外环境（时区/域名判断非国内，或 IP 属地非中国）默认直接使用全球加速域名；
 * 国内环境默认域名上传失败后，自动切换全球加速域名再试一次（无重试）；
 * 单次上传 60s 超时按失败处理；
 * COS 各域名均失败后，音频文件兜底切换阿里云 OSS 直传（非音频文件不兜底）；
 * 一旦某个桶通过加速域名上传成功，该桶后续上传都直接使用加速域名
 * @param file 待上传文件
 * @param module 业务模块名称，使用 UploadModule 常量
 * @param ext 文件扩展名，可选，默认从 file.name 或 file.type 推导
 * @param skipMsg 是否跳过错误提示
 * @returns 上传后的访问 URL
 */
const upload = async (
  file: File,
  module: UploadModuleType,
  ext?: string,
  skipMsg = false
): Promise<string> => {
  const fileExt = ext || getFileExt(file)
  if (!fileExt) {
    const err = '无法识别文件类型，请传入 ext 参数'
    !skipMsg && ElMessage({ message: '上传错误：' + err, type: 'error' })
    return Promise.reject(err)
  }

  // 调试开关：window.__USE_ALIYUN_OSS__ = true 时强制使用阿里云 OSS 上传
  if (window.__USE_ALIYUN_OSS__) {
    return uploadToAliyunOss(file, AliyunUploadModule.VOICE, fileExt, skipMsg)
  }

  let policy: PostPolicyData
  try {
    policy = await getPostPolicy(module, fileExt)
  } catch (err: any) {
    const msg = err?.msg || err?.message || '获取上传配置失败'
    !skipMsg && ElMessage({ message: '上传错误：' + msg, type: 'error' })
    return Promise.reject(msg)
  }

  const formData = new FormData()
  formData.append('key', policy.cosKey)
  formData.append('policy', policy.policy)
  formData.append('q-sign-algorithm', policy.qsignAlgorithm)
  formData.append('q-ak', policy.qak)
  formData.append('q-key-time', policy.qkeyTime)
  formData.append('q-signature', policy.qsignature)
  // file 必须是最后一个字段
  formData.append('file', file)

  // 海外环境（时区/域名判断非国内，或 IP 属地非中国）直接走加速域名；
  // 国内环境默认域名失败后自动切换全球加速域名再试一次（无重试）；
  // COS 均失败则兜底切换阿里云 OSS 直传；
  // 一旦切换成功，该桶后续上传都直接使用全球加速域名
  const accelerateHost = COS_ACCELERATE_HOST_MAP[policy.cosHost]
  if (accelerateHost && isOverseas()) acceleratedHosts.add(policy.cosHost)
  let lastErr = '网络请求失败'
  // 已切换过加速域名（或海外环境）直接使用加速域名，否则先默认域名、失败后切加速域名
  const hosts =
    accelerateHost && acceleratedHosts.has(policy.cosHost)
      ? [accelerateHost]
      : accelerateHost
        ? [policy.cosHost, accelerateHost]
        : [policy.cosHost]
  for (const host of hosts) {
    try {
      await postToCos(host, formData)
      if (host === accelerateHost) acceleratedHosts.add(policy.cosHost)
      const domain = policy.domain.endsWith('/') ? policy.domain : `${policy.domain}/`
      // 上传成功后返回腾讯云 CDN Type-A 鉴权后的访问地址
      return tryGetAuthUrl(`${domain}${policy.cosKey}`) || `${domain}${policy.cosKey}`
    } catch (err: any) {
      // AbortController.abort() 触发的是 AbortError，兼容旧浏览器的超时实现
      lastErr =
        err?.name === 'AbortError' || err?.name === 'TimeoutError'
          ? '上传超时'
          : err?.message || '网络请求失败'
    }
  }

  // COS 上传失败，音频文件兜底切换阿里云 OSS 直传（目前阿里云仅支持音频模块）
  if (isAudioFile(file, fileExt)) {
    try {
      return await uploadToAliyunOss(file, AliyunUploadModule.VOICE, fileExt, true)
    } catch (err: any) {
      lastErr = err?.message || lastErr
    }
  }

  !skipMsg && ElMessage({ message: '上传错误：' + lastErr, type: 'error' })
  return Promise.reject(lastErr)
}

export default upload
